'use client'

import React from 'react'
import Image from 'next/image'
import { cn } from '@/utilities/ui'
import { useInView } from './Reveal'

/** Single soft arrowhead (points right). Rotated per side so all point inward. */
const Arrow: React.FC = () => (
  <svg
    viewBox="0 0 57.2823 59.8333"
    fill="currentColor"
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
    className="h-full w-full"
  >
    <path d="M0.0312821 1.7426C3.74023 20.3419 3.74023 39.4917 0.0299035 58.0903C-0.210096 59.2943 1.04095 60.239 2.12095 59.6567L57.2823 29.9166L2.12232 0.176961C1.04232 -0.405963 -0.208718 0.538043 0.0312821 1.7426Z" />
  </svg>
)

/** A square arrow cell. Width is set per side so two cells span the margin band. */
const cell = 'block aspect-square shrink-0'

/** White rect outline that traces itself on, one side at a time, and always
    settles as a complete solid rectangle (each side just scales to full). */
const sideBase =
  'absolute bg-white transition-transform duration-300 ease-out motion-reduce:transition-none motion-reduce:scale-x-100 motion-reduce:scale-y-100'

const DrawnBorder: React.FC<{ drawn: boolean; delay: number }> = ({ drawn, delay }) => {
  const step = 150
  const side = (extra: string, shown: string, hidden: string, i: number) => (
    <span
      className={cn(sideBase, extra, drawn ? shown : hidden)}
      style={{ transitionDelay: `${delay + i * step}ms` }}
    />
  )
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {side('left-0 top-0 h-[1.5px] w-full origin-left', 'scale-x-100', 'scale-x-0', 0)}
      {side('right-0 top-0 h-full w-[1.5px] origin-top', 'scale-y-100', 'scale-y-0', 1)}
      {side('bottom-0 left-0 h-[1.5px] w-full origin-right', 'scale-x-100', 'scale-x-0', 2)}
      {side('bottom-0 left-0 h-full w-[1.5px] origin-bottom', 'scale-y-100', 'scale-y-0', 3)}
    </div>
  )
}

export const LogoUsage: React.FC = () => {
  const { ref, inView } = useInView<HTMLDivElement>(0.3)

  const fade = (delay: string) =>
    cn(
      'transition-opacity duration-500 ease-out motion-reduce:transition-none motion-reduce:opacity-100',
      inView ? 'opacity-100' : 'opacity-0',
      delay,
    )

  return (
    <div className="flex flex-col items-center gap-8 rounded-[16px] border border-neutral-200 bg-civo-cloud-dark p-7 md:p-9 xl:px-16 xl:py-24">
      {/* Width cap lives on the wrapper so the diagram's % padding resolves against
          the capped width (≤600), not the much wider card. */}
      <div className="group mx-auto w-full max-w-[600px]">
        {/* Clear-space diagram. The 13.4% padding *is* the clear-space margin on every
            side (= two Civo 'i' dots); two arrow cells span each margin band. The
            margin scales with the box, so it stays correct at any width or logo ratio.
            `safe-pulse` flushes the clear-space margin red on hover (see globals.css). */}
        <div ref={ref} className="safe-pulse relative w-full bg-civo-cloud p-[13.4%]">
          <DrawnBorder drawn={inView} delay={0} />

          <div className="relative z-10 flex items-center bg-civo-cloud p-0.5">
            <DrawnBorder drawn={inView} delay={750} />
            <Image
              src="/assets-rebrand/logos/civo-black.svg"
              alt="Civo logo with clear-space margins"
              width={88}
              height={36}
              unoptimized
              className={cn('h-auto w-full', fade('[transition-delay:1550ms]'))}
            />
          </div>

          {/* Arrowheads: 2 per side, soft, pointing inward; static at rest */}
          <div
            className={cn(
              'pointer-events-none absolute inset-0 z-20 text-white/40',
              fade('[transition-delay:1700ms]'),
            )}
          >
            <div className="absolute left-1/2 top-0 flex w-[6.7%] -translate-x-1/2 flex-col">
              <span className={cn(cell, 'w-full rotate-90')}>
                <Arrow />
              </span>
              <span className={cn(cell, 'w-full rotate-90')}>
                <Arrow />
              </span>
            </div>
            <div className="absolute bottom-0 left-1/2 flex w-[6.7%] -translate-x-1/2 flex-col">
              <span className={cn(cell, 'w-full -rotate-90')}>
                <Arrow />
              </span>
              <span className={cn(cell, 'w-full -rotate-90')}>
                <Arrow />
              </span>
            </div>
            <div className="absolute left-0 top-1/2 flex w-[13.4%] -translate-y-1/2">
              <span className={cn(cell, 'w-1/2')}>
                <Arrow />
              </span>
              <span className={cn(cell, 'w-1/2')}>
                <Arrow />
              </span>
            </div>
            <div className="absolute right-0 top-1/2 flex w-[13.4%] -translate-y-1/2">
              <span className={cn(cell, 'w-1/2 rotate-180')}>
                <Arrow />
              </span>
              <span className={cn(cell, 'w-1/2 rotate-180')}>
                <Arrow />
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="body-copy-medium w-full text-left text-neutral-500 md:w-auto md:whitespace-nowrap md:text-center">
        Clear space - keep a margin equal to the height of two Civo &lsquo;i&rsquo; dots on every
        side.
      </p>
    </div>
  )
}
